import React, { useState, useEffect, useMemo } from 'react';
import Modal from 'react-modal';
import { db } from '../../firebase.init';
import { collection, getDocs, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faSearch, faEdit, faTrash, faUserShield, faUser, faUserCog, faUsers, faCheck, faTimes, faSpinner, faSyncAlt,
    faExclamationTriangle, faHammer, faSort, faSortUp, faSortDown, faUserTie, faUserCheck, faUserGraduate,
    faHardHat, faClipboardCheck, faBuilding, faEye, faProjectDiagram, faPen, faMarker, faPencilAlt, faBriefcase
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { useUserRole } from '../../hooks/useUserRole';
import './Admin.css'

const ROLES = [
    { value: 'admin', label: 'Admin', color: '#dc3545', icon: faUserShield },
    { value: 'director', label: 'Director', color: '#1f01cc', icon: faUserTie },
    { value: 'manager', label: 'Manager', color: '#cc5c01', icon: faBriefcase },
    { value: 'inspection', label: 'Inspection', color: '#0d6efd', icon: faClipboardCheck },
    { value: 'coordinator', label: 'Coordinator', color: '#7d6c20', icon: faProjectDiagram },
    { value: 'remedial', label: 'Remedial', color: '#198754', icon: faHardHat },
    { value: 'inspector', label: 'Inspector', color: '#fd7e14', icon: faUserCheck },
    { value: 'carpenter', label: 'Carpenter', color: '#8B4513', icon: faHammer },
    { value: 'reviewer', label: 'Reviewer', color: '#20c997', icon: faEye },
    { value: 'annotator', label: 'Annotator', color: '#910d4a', icon: faPencilAlt },
    { value: 'client', label: 'Client', color: '#6f42c1', icon: faBuilding },
    { value: 'guest', label: 'Guest', color: '#6c757d', icon: faUser },

];

const formatUKDateTime = (dateStr) => {
    if (!dateStr || dateStr === '' || dateStr === null || dateStr === undefined) return '—';

    try {
        let d;

        if (dateStr && typeof dateStr.toDate === 'function') {
            d = dateStr.toDate();
        }
        else if (typeof dateStr === 'string') {
            if (dateStr.includes(' at ')) {
                const parts = dateStr.split(' at ');
                if (parts.length >= 2) {
                    const datePart = parts[0];
                    let timePart = parts[1];

                    const timeMatch = timePart.match(/(\d{1,2}:\d{2}:\d{2})\s*(AM|PM)?/i);
                    if (timeMatch) {
                        let timeStr = timeMatch[1];
                        const ampm = timeMatch[2];

                        let [hours, minutes, seconds] = timeStr.split(':').map(Number);

                        if (ampm && ampm.toUpperCase() === 'PM' && hours < 12) {
                            hours += 12;
                        } else if (ampm && ampm.toUpperCase() === 'AM' && hours === 12) {
                            hours = 0;
                        }

                        const isoString = `${datePart} ${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
                        d = new Date(isoString);
                    } else {
                        const cleanTimeStr = timePart.replace(/\s*UTC[+-]\d+.*$/, '').replace(/\s*GMT[+-]\d+.*$/, '');
                        d = new Date(`${datePart} ${cleanTimeStr}`);
                    }
                }
            } else {
                const cleanedStr = dateStr.replace(/\s*UTC[+-]\d+.*$/, '').replace(/\s*GMT[+-]\d+.*$/, '');
                d = new Date(cleanedStr);
            }
        }
        else if (typeof dateStr === 'number') {
            d = new Date(dateStr);
        }
        else if (dateStr && dateStr.seconds) {
            d = new Date(dateStr.seconds * 1000);
        }
        else {
            d = new Date(dateStr);
        }

        if (!d || isNaN(d.getTime())) {

            if (typeof dateStr === 'string') {
                const cleanedStr = dateStr.replace(/\s*UTC[+-]\d+.*$/, '').replace(/\s*GMT[+-]\d+.*$/, '');
                d = new Date(cleanedStr);
            }
            if (isNaN(d.getTime())) return '—';
        }

        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const year = d.getFullYear();
        const hours = String(d.getHours()).padStart(2, '0');
        const minutes = String(d.getMinutes()).padStart(2, '0');
        const seconds = String(d.getSeconds()).padStart(2, '0');

        return `${day}/${month}/${year}, ${hours}:${minutes}:${seconds}`;
    } catch (error) {
        console.error('Date parsing error for:', dateStr, error);
        return '—';
    }
};

const Admin = () => {
    const userData = useUserRole();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedRole, setSelectedRole] = useState(null);
    const [sortColumn, setSortColumn] = useState('name');
    const [sortDirection, setSortDirection] = useState('asc');
    const [saving, setSaving] = useState(false);

    const [editingUser, setEditingUser] = useState(null);
    const [editForm, setEditForm] = useState({ name: '', email: '', user_role: '' });
    const [deletingUser, setDeletingUser] = useState(null);

    useEffect(() => {
        if (userData.ready) fetchUsers();
    }, [userData.ready]);

    const filteredBySearchAndRole = useMemo(() => {
        let result = users;
        const term = searchTerm.toLowerCase().trim();
        if (term) {
            result = result.filter(
                (u) =>
                    u.name?.toLowerCase().includes(term) ||
                    u.email?.toLowerCase().includes(term) ||
                    u.uid?.toLowerCase().includes(term) ||
                    u.user_role?.toLowerCase().includes(term)
            );
        }
        if (selectedRole) {
            result = result.filter((u) => u.user_role === selectedRole);
        }
        return result;
    }, [users, searchTerm, selectedRole]);

    const sortedUsers = useMemo(() => {
        const sorted = [...filteredBySearchAndRole];
        sorted.sort((a, b) => {
            let valA, valB;
            switch (sortColumn) {
                case 'name':
                    valA = (a.name || '').toLowerCase();
                    valB = (b.name || '').toLowerCase();
                    break;
                case 'email':
                    valA = (a.email || '').toLowerCase();
                    valB = (b.email || '').toLowerCase();
                    break;
                case 'role':
                    valA = (a.user_role || '').toLowerCase();
                    valB = (b.user_role || '').toLowerCase();
                    break;
                case 'lastLogin':
                    valA = a.lastLoginAt ? new Date(a.lastLoginAt).getTime() : 0;
                    valB = b.lastLoginAt ? new Date(b.lastLoginAt).getTime() : 0;
                    break;
                case 'created':
                    valA = a.creationTime ? new Date(a.creationTime).getTime() : 0;
                    valB = b.creationTime ? new Date(b.creationTime).getTime() : 0;
                    break;
                default:
                    return 0;
            }
            if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
            if (valA > valB) return sortDirection === 'asc' ? 1 : -1;
            return 0;
        });
        return sorted;
    }, [filteredBySearchAndRole, sortColumn, sortDirection]);

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const snap = await getDocs(collection(db, 'users'));
            const usersList = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
            setUsers(usersList);
        } catch (error) {
            console.error('Error fetching users:', error);
            toast.error('Failed to load users');
        } finally {
            setLoading(false);
        }
    };

    const handleCardClick = (roleValue) => {
        setSelectedRole((prev) => (prev === roleValue ? null : roleValue));
    };

    const handleSort = (column) => {
        if (sortColumn === column) {
            setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortColumn(column);
            setSortDirection('asc');
        }
    };

    const openEditModal = (user) => {
        setEditingUser(user);
        setEditForm({
            name: user.name || '',
            email: user.email || '',
            user_role: user.user_role || 'guest',
        });
    };
    const closeEditModal = () => setEditingUser(null);

    const handleSaveEdit = async () => {
        if (!editingUser) return;
        setSaving(true);
        try {
            await updateDoc(doc(db, 'users', editingUser.uid), {
                name: editForm.name,
                email: editForm.email,
                user_role: editForm.user_role,
            });
            setUsers((prev) =>
                prev.map((u) =>
                    u.uid === editingUser.uid ? { ...u, ...editForm } : u
                )
            );
            toast.success('User updated successfully!');
            closeEditModal();
        } catch (error) {
            console.error('Error updating user:', error);
            toast.error('Failed to update user');
        } finally {
            setSaving(false);
        }
    };

    const openDeleteModal = (user) => setDeletingUser(user);
    const closeDeleteModal = () => setDeletingUser(null);

    const handleDelete = async () => {
        if (!deletingUser) return;
        try {
            await deleteDoc(doc(db, 'users', deletingUser.uid));
            setUsers((prev) => prev.filter((u) => u.uid !== deletingUser.uid));
            toast.success('User deleted successfully!');
            closeDeleteModal();
        } catch (error) {
            console.error('Error deleting user:', error);
            toast.error('Failed to delete user');
        }
    };

    const getRoleInfo = (role) => ROLES.find((r) => r.value === role) || ROLES[ROLES.length - 1];

    const renderSortIcon = (column) => {
        if (sortColumn !== column) return <FontAwesomeIcon icon={faSort} className="ms-1 text-muted opacity-50" />;
        return sortDirection === 'asc' ? (
            <FontAwesomeIcon icon={faSortUp} className="ms-1 text-primary" />
        ) : (
            <FontAwesomeIcon icon={faSortDown} className="ms-1 text-primary" />
        );
    };

    if (!userData.ready) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                <div className="text-center">
                    <FontAwesomeIcon icon={faSpinner} spin size="3x" className="text-primary mb-3" />
                    <p className="text-muted fs-5">Loading user data...</p>
                </div>
            </div>
        );
    }

    if (userData.role !== 'admin') {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                <div className="text-center">
                    <FontAwesomeIcon icon={faUserShield} size="4x" className="text-danger mb-3" />
                    <h3>Access Denied</h3>
                    <p className="text-muted">You do not have permission to view this page.</p>
                </div>
            </div>
        );
    }

    const customModalStyles = {
        overlay: {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1050,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        },
        content: {
            position: 'relative',
            top: 'auto',
            left: 'auto',
            right: 'auto',
            bottom: 'auto',
            border: 'none',
            borderRadius: '0.75rem',
            padding: '0',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
            background: '#fff',
        },
    };

    return (
        <div className="container-fluid px-3 px-md-4 py-4" style={{ maxWidth: '1650px' }}>

            <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4 gap-3">
                <div>
                    <h2 className="fw-bold mb-1 d-flex align-items-center gap-2">
                        <FontAwesomeIcon icon={faUserShield} className="text-danger" />
                        Admin Panel
                    </h2>
                    <p className="text-muted mb-0 ms-4 ps-2">Manage accounts, roles, and permissions</p>
                </div>
                <div className="d-flex align-items-center gap-2 gap-md-3 flex-wrap">
                    <span className="badge bg-danger rounded-pill px-3 py-2" style={{ fontSize: '0.85rem' }}>
                        <FontAwesomeIcon icon={faUsers} className="me-2" />
                        {users.length} User{users.length !== 1 ? 's' : ''}
                    </span>
                    <button
                        className="btn btn-outline-danger rounded-pill d-flex align-items-center gap-2"
                        onClick={fetchUsers}
                        style={{ fontSize: '0.9rem' }}
                    >
                        <FontAwesomeIcon icon={faSyncAlt} />
                        <span className="d-none d-sm-inline">Refresh</span>
                    </button>
                </div>
            </div>

            <div className="row g-2 g-md-3 justify-content-center mb-4">
                {ROLES.map((role) => {
                    const count = users.filter((u) => u.user_role === role.value).length;
                    const isSelected = selectedRole === role.value;
                    return (
                        <div key={role.value} className="col-4 col-sm-3 col-md-2 col-lg">
                            <div
                                className={`card border-0 text-white h-100 rounded-4 shadow-sm position-relative ${isSelected ? 'border-2 border-white shadow-lg' : ''
                                    }`}
                                style={{
                                    background: role.color,
                                    cursor: 'pointer',
                                    transition: 'transform 0.2s, box-shadow 0.2s',
                                    transform: isSelected ? 'scale(1.04)' : 'scale(1)',
                                    boxShadow: isSelected
                                        ? '0 0 0 3px #ffffff, 0 10px 20px rgba(0,0,0,0.3)'
                                        : undefined,
                                }}
                                onClick={() => handleCardClick(role.value)}
                                onMouseEnter={(e) => {
                                    if (!isSelected) e.currentTarget.style.transform = 'scale(1.02)';
                                }}
                                onMouseLeave={(e) => {
                                    if (!isSelected) e.currentTarget.style.transform = 'scale(1)';
                                }}
                            >
                                <div className="card-body d-flex justify-content-between align-items-center p-2 p-md-3">
                                    <div className="me-1" style={{ minWidth: 0 }}>
                                        <small
                                            className="text-uppercase fw-semibold d-block mb-1 text-truncate"
                                            style={{ letterSpacing: '0.3px', opacity: 0.9, fontSize: 'clamp(0.55rem, 1.5vw, 0.7rem)', maxWidth: '100%' }}
                                        >
                                            {role.label}
                                        </small>
                                        <h5 className="mb-0 fw-bold" style={{ fontSize: 'clamp(0.8rem, 2.5vw, 1.1rem)' }}>{count}</h5>
                                    </div>
                                    <div className="d-flex align-items-center flex-shrink-0">
                                        <FontAwesomeIcon icon={role.icon} style={{ opacity: 0.6, fontSize: 'clamp(1rem, 3vw, 1.5rem)' }} />
                                    </div>
                                </div>
                                {isSelected && (
                                    <div
                                        className="position-absolute top-0 end-0 mt-1 me-1 bg-white bg-opacity-25 rounded-pill px-1 px-md-2 py-1 d-flex align-items-center gap-1"
                                        style={{ fontSize: '0.6rem', backdropFilter: 'blur(4px)' }}
                                    >
                                        <FontAwesomeIcon icon={faTimes} />
                                        <span className="d-none d-md-inline">Clear</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="card border-0 shadow-sm rounded-4 mb-4">
                <div className="card-body py-3 px-3">
                    <div className="input-group shadow-sm h-100 position-relative">
                        <input
                            type="text"
                            className="form-control border-danger"
                            placeholder="Search by name, email, or role..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{
                                height: '48px',
                                fontSize: '0.95rem',
                                borderRight: 'none',
                                paddingRight: searchTerm ? '32px' : '12px'
                            }}
                            aria-label="Search users"
                        />

                        {searchTerm && (
                            <span
                                onClick={() => setSearchTerm('')}
                                style={{
                                    position: 'absolute',
                                    right: '52px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    zIndex: 10,
                                    cursor: 'pointer',
                                    color: '#6c757d',
                                    fontSize: '16px',
                                    lineHeight: 1,
                                    padding: '4px',
                                }}
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </span>
                        )}

                        <span
                            className="input-group-text bg-danger text-white border-danger"
                            style={{ cursor: 'pointer', height: '48px' }}
                            role="button"
                            tabIndex="0"
                            aria-label="Search"
                        >
                            <FontAwesomeIcon icon={faSearch} />
                        </span>
                    </div>

                    <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
                        {selectedRole && (
                            <small className="text-muted">
                                Filtering by role: <strong>{ROLES.find(r => r.value === selectedRole)?.label}</strong>
                                <button
                                    className="btn btn-link btn-sm p-0 ms-2 text-danger"
                                    onClick={() => setSelectedRole(null)}
                                >
                                    Clear filter
                                </button>
                            </small>
                        )}
                        <small className="text-muted ms-auto">
                            Sorted by: <strong>{sortColumn}</strong> ({sortDirection === 'asc' ? 'A‑Z' : 'Z‑A'})
                        </small>
                    </div>
                </div>
            </div>

            <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
                <div className="card-body p-0">
                    {loading ? (
                        <div className="text-center py-5">
                            <FontAwesomeIcon icon={faSpinner} spin size="2x" className="text-danger mb-3" />
                            <p className="text-muted mb-0">Loading users...</p>
                        </div>
                    ) : (
                        <div
                            className="table-responsive"
                            style={{ maxHeight: 'calc(100vh - 400px)', overflowY: 'auto' }}
                        >
                            <table className="table table-hover align-middle mb-0">
                                <thead
                                    className="table-light"
                                    style={{ position: 'sticky', top: 0, zIndex: 2 }}
                                >
                                    <tr>
                                        <th
                                            className="ps-4 py-3 text-start"
                                            style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
                                            onClick={() => handleSort('name')}
                                        >
                                            User {renderSortIcon('name')}
                                        </th>
                                        <th
                                            className="py-3 text-start"
                                            style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
                                            onClick={() => handleSort('email')}
                                        >
                                            Email {renderSortIcon('email')}
                                        </th>
                                        <th
                                            className="py-3 text-center"
                                            style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
                                            onClick={() => handleSort('role')}
                                        >
                                            Role {renderSortIcon('role')}
                                        </th>
                                        <th
                                            className="py-3 text-center"
                                            style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
                                            onClick={() => handleSort('lastLogin')}
                                        >
                                            Last Login {renderSortIcon('lastLogin')}
                                        </th>
                                        <th
                                            className="py-3 text-center"
                                            style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
                                            onClick={() => handleSort('created')}
                                        >
                                            Created {renderSortIcon('created')}
                                        </th>
                                        <th className="pe-4 py-3 text-center" style={{ whiteSpace: 'nowrap' }}>
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedUsers.map((user) => {
                                        const roleInfo = getRoleInfo(user.user_role);
                                        return (
                                            <tr key={user.uid}>
                                                <td className="ps-4 text-start">
                                                    <div className="d-flex align-items-center gap-3">
                                                        <div
                                                            className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                                                            style={{
                                                                width: '38px',
                                                                height: '38px',
                                                                backgroundColor: `${roleInfo.color}20`,
                                                                color: roleInfo.color,
                                                            }}
                                                        >
                                                            <FontAwesomeIcon icon={roleInfo.icon} size="sm" />
                                                        </div>
                                                        <span className="fw-semibold text-dark text-truncate" style={{ maxWidth: '120px' }}>
                                                            {user.name || 'Unnamed User'}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="text-start text-secondary small text-truncate" style={{ maxWidth: '150px' }}>
                                                    {user.email || '—'}
                                                </td>
                                                <td className="text-center">
                                                    <span
                                                        className="badge rounded-pill px-3 py-2 fw-semibold"
                                                        style={{ backgroundColor: `${roleInfo.color}20`, color: roleInfo.color }}
                                                    >
                                                        {roleInfo.label}
                                                    </span>
                                                </td>
                                                <td className="text-center small text-muted text-nowrap">
                                                    {formatUKDateTime(user.lastLoginAt)}
                                                </td>
                                                <td className="text-center small text-muted text-nowrap">
                                                    {formatUKDateTime(user.creationTime)}
                                                </td>
                                                <td className="pe-4 text-center">
                                                    <div className="d-flex gap-2 justify-content-center">
                                                        <button
                                                            className="btn btn-sm btn-outline-danger rounded-pill d-flex align-items-center gap-1 px-3"
                                                            onClick={() => openEditModal(user)}
                                                        >
                                                            <FontAwesomeIcon icon={faEdit} />
                                                            Edit
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                    {sortedUsers.length === 0 && (
                                        <tr>
                                            <td colSpan="6" className="text-center py-5 text-muted">
                                                {selectedRole || searchTerm
                                                    ? 'No users match the current filters.'
                                                    : 'No users found.'}
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            <Modal
                isOpen={editingUser !== null}
                onRequestClose={closeEditModal}
                style={customModalStyles}
                contentLabel="Edit User"
            >
                <div className="p-4">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h5 className="fw-bold mb-0">
                            <FontAwesomeIcon icon={faEdit} className="me-2 text-danger" />
                            Edit User
                        </h5>
                        <button type="button" className="btn-close" onClick={closeEditModal} />
                    </div>
                    <div className="mb-3">
                        <label className="form-label fw-semibold small text-secondary">Name</label>
                        <input
                            type="text"
                            className="form-control rounded-3 border-danger"
                            value={editForm.name}
                            onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                        />
                    </div>
                    <div className="mb-3">
                        <label className="form-label fw-semibold small text-secondary">Email</label>
                        <input
                            type="email"
                            className="form-control rounded-3 border-danger"
                            value={editForm.email}
                            onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                        />
                    </div>
                    <div className="mb-4">
                        <label className="form-label fw-semibold small text-secondary">Role</label>
                        <select
                            className="form-select rounded-3 border-danger"
                            value={editForm.user_role}
                            onChange={(e) => setEditForm({ ...editForm, user_role: e.target.value })}
                        >
                            {ROLES.map((role) => (
                                <option key={role.value} value={role.value}>
                                    {role.label}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="d-flex justify-content-end gap-2">
                        <button className="btn btn-light rounded-pill px-4" onClick={closeEditModal} disabled={saving}>
                            Cancel
                        </button>
                        <button
                            className="btn btn-danger rounded-pill px-4"
                            onClick={handleSaveEdit}
                            disabled={saving}
                        >
                            {saving ? (
                                <>
                                    <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                                    Saving...
                                </>
                            ) : (
                                'Save Changes'
                            )}
                        </button>
                    </div>
                </div>
            </Modal>

            <Modal
                isOpen={deletingUser !== null}
                onRequestClose={closeDeleteModal}
                style={customModalStyles}
                contentLabel="Delete User"
            >
                <div className="p-4 text-center">
                    <div
                        className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                        style={{ width: '64px', height: '64px', background: '#ffe7e7' }}
                    >
                        <FontAwesomeIcon icon={faExclamationTriangle} size="2x" style={{ color: '#dc3545' }} />
                    </div>
                    <h5 className="fw-bold mb-2 text-danger">Delete User?</h5>
                    <p className="text-muted mb-3">
                        This action cannot be undone.<br />
                        <strong>{deletingUser?.name}</strong> will be permanently removed.
                    </p>
                    <div className="d-flex justify-content-center gap-2">
                        <button className="btn btn-light rounded-pill px-4" onClick={closeDeleteModal}>
                            Cancel
                        </button>
                        <button className="btn btn-danger rounded-pill px-4" onClick={handleDelete}>
                            <FontAwesomeIcon icon={faTrash} className="me-1" />
                            Delete
                        </button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default Admin;